import * as THREE from 'three';
import { SIGNAL } from '../assets/MaterialLibrary';

export type VfxEvent =
  | { type: 'pickup'; position: THREE.Vector3; color?: string }
  | { type: 'gate'; position: THREE.Vector3 }
  | { type: 'splash'; position: THREE.Vector3; strength: number }
  | { type: 'boost'; position: THREE.Vector3 }
  | { type: 'boostPickup'; position: THREE.Vector3 }
  | { type: 'lap'; position: THREE.Vector3 };

interface Particle {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
  gravity: number;
  spin: number;
  shrink: number;
}

// Event-driven VFX: pooled particles, no permanent clutter.
export class VfxSystem {
  private readonly pool: Particle[] = [];
  private readonly active: Particle[] = [];
  private readonly splashMaterial = new THREE.MeshBasicMaterial({
    color: '#dff3ff', transparent: true, opacity: 0.85,
  });
  private readonly boostMaterial = new THREE.MeshBasicMaterial({
    color: SIGNAL.boost, transparent: true, opacity: 0.9,
  });
  private readonly rewardMaterial = new THREE.MeshBasicMaterial({
    color: SIGNAL.reward, transparent: true, opacity: 0.95,
  });
  private readonly ringMaterial = new THREE.MeshBasicMaterial({
    color: SIGNAL.objective, transparent: true, opacity: 0.9, side: THREE.DoubleSide,
  });
  private readonly dropletGeometry = new THREE.TetrahedronGeometry(0.16);
  private readonly ringGeometry = new THREE.RingGeometry(0.5, 0.72, 32);

  constructor(private readonly scene: THREE.Scene, private readonly maxParticles = 260) {}

  emit(event: VfxEvent): void {
    switch (event.type) {
      case 'pickup':
        this.burst(event.position, this.rewardMaterial, 14, 7, 0.9, 5);
        break;
      case 'gate':
        this.ringPulse(event.position, SIGNAL.objective);
        this.burst(event.position, this.rewardMaterial, 10, 5, 0.7, 4);
        break;
      case 'lap':
        this.ringPulse(event.position, SIGNAL.reward);
        this.burst(event.position, this.rewardMaterial, 26, 10, 1.3, 6);
        break;
      case 'splash':
        this.burst(event.position, this.splashMaterial, Math.round(4 + event.strength * 10), 3 + event.strength * 5, 0.6, 9, event.strength * 0.1);
        break;
      case 'boost':
        this.burst(event.position, this.boostMaterial, 8, 9, 0.5, 2, -0.1);
        break;
      case 'boostPickup':
        this.burst(event.position, this.boostMaterial, 18, 8, 0.8, 4);
        this.ringPulse(event.position, SIGNAL.boost);
        break;
    }
  }

  private burst(
    position: THREE.Vector3,
    material: THREE.Material,
    count: number,
    speed: number,
    life: number,
    gravity: number,
    upwardBias = 2.2,
  ): void {
    for (let i = 0; i < count; i++) {
      if (this.active.length >= this.maxParticles) return;
      const particle = this.acquire();
      if (!particle) return;
      particle.mesh.material = material;
      particle.mesh.position.copy(position);
      const a = (i / count) * Math.PI * 2;
      const s = speed * (0.4 + Math.random() * 0.6);
      particle.velocity.set(
        Math.cos(a) * s * 0.6,
        Math.random() * upwardBias + 0.5,
        Math.sin(a) * s * 0.6,
      );
      particle.life = 0;
      particle.maxLife = life * (0.7 + Math.random() * 0.5);
      particle.gravity = gravity;
      particle.spin = Math.random() * 8 - 4;
      particle.shrink = 1;
      particle.mesh.scale.setScalar(0.7 + Math.random() * 0.7);
      particle.mesh.visible = true;
      this.active.push(particle);
    }
  }

  private ringPulse(position: THREE.Vector3, color: string): void {
    const ring = new THREE.Mesh(this.ringGeometry, this.ringMaterial.clone());
    (ring.material as THREE.MeshBasicMaterial).color.set(color);
    ring.position.copy(position);
    ring.rotation.x = -Math.PI / 2;
    ring.scale.setScalar(0.3);
    this.scene.add(ring);
    const start = performance.now();
    const expand = () => {
      const t = (performance.now() - start) / 450;
      if (t >= 1) {
        this.scene.remove(ring);
        (ring.material as THREE.Material).dispose();
        return;
      }
      ring.scale.setScalar(0.3 + t * 4.5);
      (ring.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - t);
      requestAnimationFrame(expand);
    };
    requestAnimationFrame(expand);
  }

  private acquire(): Particle | null {
    let particle = this.pool.pop();
    if (!particle) {
      if (this.active.length + this.pool.length >= this.maxParticles) return null;
      const mesh = new THREE.Mesh(this.dropletGeometry, this.splashMaterial);
      mesh.frustumCulled = false;
      this.scene.add(mesh);
      particle = { mesh, velocity: new THREE.Vector3(), life: 0, maxLife: 1, gravity: 9, spin: 0, shrink: 1 };
    }
    return particle;
  }

  private release(particle: Particle): void {
    particle.mesh.visible = false;
    this.pool.push(particle);
  }

  update(delta: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i];
      p.life += delta;
      if (p.life >= p.maxLife) {
        this.release(p);
        this.active.splice(i, 1);
        continue;
      }
      p.velocity.y -= p.gravity * delta;
      p.mesh.position.addScaledVector(p.velocity, delta);
      p.mesh.rotation.x += p.spin * delta;
      p.mesh.rotation.y += p.spin * 0.7 * delta;
      const k = 1 - p.life / p.maxLife;
      p.mesh.scale.setScalar(Math.max(0.01, k * p.shrink));
    }
  }

  dispose(): void {
    for (const p of [...this.active, ...this.pool]) {
      this.scene.remove(p.mesh);
    }
    this.active.length = 0;
    this.pool.length = 0;
    this.dropletGeometry.dispose();
    this.ringGeometry.dispose();
    this.splashMaterial.dispose();
    this.boostMaterial.dispose();
    this.rewardMaterial.dispose();
    this.ringMaterial.dispose();
  }
}
